import { useEffect } from 'react'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import BottomSheetContent from '.././BottomSheetContent'
import RenderHTML from '../../ui-components/RenderHTML'
import { TertiaryButton } from '../../execution/TertiaryButton'
import { PrimaryButton } from '../../execution/PrimaryButton'
import { mfLogoBaseUrl } from '../../../env'
import { toPascalCaseWithSpaces } from '../../../utils/utils'

type BottomSheetWrapperPropTypes = {
  onDismiss: () => void
  ScreenName: string
  type: 'OFF_TRACK' | 'OUT_OF_FORM'
  mfLogo: string
  onInvest: () => void
  fundCategory?: string
}

export const NotRecommendedFund = ({ onDismiss, ScreenName, type, mfLogo, onInvest, fundCategory }: BottomSheetWrapperPropTypes) => {
  const heading = type === 'OFF_TRACK' ? 'Not Recommended: Off-track' : 'Not Recommended: Out-of-form'
  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        eventId: heading,
        bottomSheetName: type === 'OFF_TRACK' ? 'Off track alert' : 'Out of form alert',
        ScreenName
      }
    })
  }, [])
  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      bottomSheetCtas={
        <div className='flex items-center gap-2 w-full h-[56px]'>
          <div className='flex-1 h-full'>
            <TertiaryButton
              testLocator='BS_CTA_SECONDARY'
              label='Close'
              onClick={onDismiss}
              tertiaryButtonClassName='h-full text-[16px]'
            />
          </div>
          <div className='flex-1 h-full'>
            <PrimaryButton
              testLocator='BS_CTA_PRIMARY'
              label='Invest anyway'
              onClick={onInvest}
              primaryButtonClassName='h-full text-[16px]'
            />
          </div>
        </div>
      }
    >
      <div className='leading-[120%] flex flex-col'>
        {mfLogo && (
          <div className='flex items-center pb-6'>
            <div className='rounded-full h-[53px] w-[53px] flex items-center justify-center border border-[#EBF2F2] overflow-hidden'>
              <img id='mutualFundLogo' src={`${mfLogoBaseUrl}${mfLogo}`} className='h-10 w-10' />
            </div>
            <div className='-ml-[14px] scale-95'>
              <RenderHTML innerHTMLText={type === 'OFF_TRACK' ? OffTrackIcon : OutOfFormIcon} />
            </div>
          </div>
        )}
        <div style={{ color: type === 'OFF_TRACK' ? '#995400' : '#992617' }} className='pb-2'>
          Fund is <span className='font-[500]'> {type === 'OFF_TRACK' ? 'Off-track' : 'Out-of-form'}</span>
        </div>
        <div className='w-[65%] text-[#232323] leading-[130%] text-[21px] font-[500] pb-4'>
          Not recommended for investing right now
        </div>
        <div className='pb-6 text-[#6A6E6E] text-[13px] font-[400] leading-[150%] w-[95%]'>
          This fund has not been performing well compared to other funds in its category.{' '}
        </div>
        <div className='flex items-center gap-4 border-t border-b border-[#EBF2F2] py-4'>
          <div className='bg-[#F0F4F7] rounded-full p-2'>
            <RenderHTML innerHTMLText={BulbIcon} />
          </div>
          <div className='text-[14px] w-[85%]'>
            Consider investing in In-form funds <br /> {fundCategory ? 'in ' : 'of '}
            <span className='font-[500]'>{toPascalCaseWithSpaces(fundCategory ?? 'the same category')}</span>
          </div>
        </div>
      </div>
    </BottomSheetContent>
  )
}

const BulbIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
  <g clip-path="url(#clip0_37231_1620)">
    <path d="M15.6671 14.0334C16.1739 13.2562 16.4905 12.1379 16.5327 11.2111C16.5327 7.87087 14.6538 5.99197 10.9207 5.99197C7.58043 5.99197 5.30871 7.87087 5.30871 11.2111C5.35082 12.1379 5.66747 13.2562 6.17425 14.0334C7.07928 15.4211 8.21774 16.5087 8.21774 18.1602C9.01244 17.991 9.46881 17.9064 10.9207 17.9064C11.9797 17.9064 12.829 17.991 13.6236 18.1602C13.6236 16.5087 14.7621 15.4211 15.6671 14.0334Z" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M13.62 18.1578C13.6232 18.2653 13.625 18.3734 13.625 18.4818C13.625 18.9682 13.5895 19.4453 13.5473 19.9103C13.518 20.2177 13.4013 20.5043 13.2164 20.7233C13.0315 20.9423 12.7893 21.0808 12.5292 21.1163C12.1335 21.1679 11.335 21.2129 10.9208 21.2129C10.5066 21.2129 9.70768 21.1683 9.31234 21.1159C9.05227 21.0804 8.81003 20.9419 8.62514 20.7228C8.44024 20.5038 8.32361 20.2172 8.29424 19.9098C8.25208 19.4458 8.21658 18.9686 8.21658 18.4818C8.21658 18.3734 8.21834 18.2654 8.22155 18.1579C9.01522 17.9892 9.86349 17.9049 10.9209 17.9049C12.3705 17.9049 12.8277 17.9892 13.62 18.1578Z" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M10.9214 14.8021L10.9214 17.9043" stroke="black" stroke-width="1.57143" stroke-linecap="round"/>
    <path d="M12.644 12.7037C12.3442 13.413 11.9137 14.0513 11.5304 14.5133C11.2038 14.907 10.638 14.907 10.3114 14.5133C9.92808 14.0513 9.4976 13.413 9.19774 12.7037" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M18.2227 3.77705L17.6208 4.37891" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M11 0.785475L11 1.63672" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M0.785633 11L1.63672 11" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M20.3632 11L21.2144 11" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M3.77754 3.779L4.37939 4.38086" stroke="black" stroke-width="1.57143" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_37231_1620">
      <rect width="22" height="22" fill="white" transform="translate(22 22) rotate(-180)"/>
    </clipPath>
  </defs>
</svg>`

const OutOfFormIcon = `<svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="25.6016" cy="25" r="24.5" fill="#FFE9E6" stroke="#FBD5D0"/>
<path d="M18.4299 20.6475C17.8638 20.1622 17.7982 19.3102 18.2834 18.7441C18.7686 18.1782 19.6207 18.1125 20.1867 18.5977L25.6076 23.2451L31.0295 18.5977C31.5955 18.1125 32.4476 18.1782 32.9328 18.7441C33.418 19.3102 33.3524 20.1622 32.7863 20.6475L25.6076 26.8008L18.4299 20.6475ZM18.4299 27.8467C17.8638 27.3615 17.7982 26.5094 18.2834 25.9434C18.7686 25.3775 19.6207 25.3117 20.1867 25.7969L25.6076 30.4443L31.0295 25.7969C31.5955 25.3118 32.4476 25.3774 32.9328 25.9434C33.418 26.5094 33.3524 27.3615 32.7863 27.8467L25.6076 34L18.4299 27.8467Z" fill="#992617"/>
</svg>
`

const OffTrackIcon = `<svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="25" cy="25" r="24.5" fill="#FFE2BF" stroke="white"/>
<path d="M31.8808 25.6475C32.4234 25.1203 32.4864 24.1941 32.0214 23.5791C31.5563 22.9641 30.7389 22.8929 30.1962 23.4199L26.4619 27.0479V19.1338C26.4618 18.3238 25.805 17.6671 24.9951 17.667C24.185 17.667 23.5283 18.3238 23.5283 19.1338V27.04L19.8027 23.4199C19.26 22.8928 18.4426 22.9641 17.9775 23.5791C17.5123 24.1941 17.5754 25.1203 18.1181 25.6475L24.999 32.332L31.8808 25.6475Z" fill="#995400"/>
</svg>
`
